// ./src/hooks/useVenueCategories.ts
//
// Loads the global venue-category vocabulary once (read-only reference data) so
// the venue drawer / filter / bulk modals can offer a category multi-select. Not
// a full CRUD hook — categories are managed elsewhere.
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";

import { VenueCategory, VenueCategoryService, listAllPages } from "@/utils/api";
import { useApp } from "@/contexts";
import { useRefreshOnWriteTimeout } from "./useRefreshOnWriteTimeout";

export interface UseVenueCategoriesReturn {
  categories: VenueCategory[];
  loading: boolean;
  isLoaded: boolean;
  load: () => Promise<OpResult>;
  getById: (id: string | undefined) => VenueCategory | undefined;
  lastError: OpResult | null;
}

export interface UseVenueCategoriesOptions {
  autoLoad?: boolean;
}

export const useVenueCategories = (options: UseVenueCategoriesOptions = {}): UseVenueCategoriesReturn => {
  const { autoLoad = true } = options;

  const { user } = useApp();
  const userId = user?.id ?? null;

  const [categories, setCategories] = useState<VenueCategory[]>([]);
  /**
   * ⚠⚠ STARTS TRUE WHEN THIS HOOK IS GOING TO AUTO-LOAD — "about to read" is not "read, and empty".
   *
   * The auto-load effect is gated on `userId`, which arrives ASYNCHRONOUSLY from `AppContext`. So on
   * a hard reload or a first navigation there are renders where no request has started yet: with
   * `loading` false and the collection empty, every list component's
   * `!loading && items.length === 0` branch is TRUE and paints the first-run empty state before the
   * skeleton and then the data. Reported across the whole event space (owner, 2026-09-28).
   * ⓘ Fixed HERE rather than in the ~45 list components that read it: the lie is in the flag, not in
   * the branch, and a per-component fix would have to be remembered by every list written after it.
   * ⚠ `autoLoad: false` still starts FALSE — a hook told not to load must not claim to be loading.
   */
  const [loading, setLoading] = useState(autoLoad);
  const [isLoaded, setIsLoaded] = useState(false);
  const [lastError, setLastError] = useState<OpResult | null>(null);

  const load = useCallback(async (): Promise<OpResult> => {
    setLoading(true);
    setLastError(null);

    try {
      const result = await listAllPages(VenueCategoryService.list, {}, [
        { name: "categoryGroup", order: "ASC" },
        { name: "name", order: "ASC" },
      ]);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        setCategories(Array.isArray(result.data) ? (result.data as VenueCategory[]) : []);
        setIsLoaded(true);
        setLastError(null);

        return result;
      }

      setLastError(result);
      setIsLoaded(true);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);
      setIsLoaded(true);

      return result;
    } finally {
      setLoading(false);
    }
  }, []);

  // ROADMAP #131 — a save that timed out may still have landed: re-read what is shown.
  useRefreshOnWriteTimeout(load, isLoaded);

  const getById = useCallback(
    (id: string | undefined): VenueCategory | undefined => {
      if (!id) {
        return undefined;
      }

      return categories.find((c) => c.id === id);
    },
    [categories]
  );

  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (autoLoad && userId) {
      loadRef.current();
    }
  }, [autoLoad, userId]);

  return useMemo(
    () => ({ categories, loading, isLoaded, load, getById, lastError }),
    [categories, loading, isLoaded, load, getById, lastError]
  );
};
